import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { useDropzone } from "react-dropzone";
import {
  ArrayField,
  Button,
  Datagrid,
  FunctionField,
  TextField,
  useNotify,
} from "react-admin";

import {
  AddAllIcon,
  AttachFileIcon,
  CancelIcon,
  CloseIcon,
} from "../../../components/common/iconography";
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MSG } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { fetchCarrierIdentities } from "../../../utils/carrierIdentity";
import {
  CARRIER_IMPORT_FIELDS,
  guessFieldMap,
  missingRequiredMappings,
  parseCarrierCSV,
  planCarrierImport,
} from "./carrierImportUtils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    justifyContent: "space-between",
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  dialogContent: {
    height: "90vh",
  },
  dropZone: {
    background: theme.palette.background.default,
    cursor: "pointer",
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    color: theme.palette.text.secondary,
    transition: "background 0.2s ease-in-out",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  activeDropZone: {
    background: theme.palette.action.selected,
  },
  caption: {
    marginTop: theme.spacing(1),
  },
  fakeFieldset: {
    position: "relative",
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  fakeLegend: {
    position: "absolute",
    top: -theme.spacing(2),
    left: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    padding: "0 4px",
  },
  scrollableGrid: {
    overflowX: "auto",
    width: "100%",
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
}));

/**
 * Upload a CSV of carriers, match its columns to carrier fields, and review what
 * will be created before anything is written.
 *
 * Every importable row becomes a carrier and an invite to its address, so the
 * dialog's job is to make the skipped rows visible first: duplicates of the
 * yard's existing carriers (archived ones included), duplicates within the file,
 * and rows with no name or no address. The manager corrects the spreadsheet and
 * re-uploads rather than the import guessing on their behalf.
 */
export const CarrierImportDialog = ({ isOpen, setIsOpen, handleImport }) => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [csvData, setCSVData] = useState([]);
  const [csvErrors, setCSVErrors] = useState([]);
  const [columns, setColumns] = useState([]);
  const [carriersFile, setCarriersFile] = useState(null);
  const [fieldMap, setFieldMap] = useState({});
  const [existingCarriers, setExistingCarriers] = useState([]);

  useEffect(() => {
    let stale = false;
    const loadBook = async () => {
      try {
        const carriers = await fetchCarrierIdentities(
          window.appUser?.reloadYardId,
        );
        if (!stale) setExistingCarriers(carriers);
      } catch (error) {
        // The preview loses its duplicate column; the import itself re-checks
        // against a fresh fetch and refuses to run if that one fails too, so a
        // failure here costs review rather than correctness.
        logError(
          "managerApp:carriers:CarrierImportDialog:loadBook",
          error,
          false,
        );
        if (!stale)
          notify(
            "Could not load existing carriers — duplicates will be checked on import.",
            "warning",
          );
      }
    };
    loadBook();
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every row is checked against the yard's whole carrier book and against the
  // rows already accepted from this file, so a large upload costs a pass over
  // both. Planning it in the render body spent that on every keystroke, dialog
  // state change and Datagrid update; it depends on nothing else, so it is
  // planned once per file, mapping or carrier-book change.
  const { decisions, importable, skipped } = useMemo(
    () => planCarrierImport(csvData, fieldMap, existingCarriers),
    [csvData, fieldMap, existingCarriers],
  );

  // The preview renders one Status cell per row, and each was scanning the
  // whole decision list for its own verdict — the file's row count squared.
  // Indexed once per plan instead.
  const reasonByRowIndex = useMemo(
    () =>
      new Map(
        decisions.map((decision) => [decision.rowIndex, decision.reason]),
      ),
    [decisions],
  );

  const mappingErrors =
    csvData.length > 0 ? missingRequiredMappings(fieldMap) : [];

  /** Forget the loaded file entirely — its name included. Anything less leaves
   * the dialog describing a file it is not holding: a "Selected: …" line above
   * an empty preview reads as a file that loaded, and rows left from an earlier
   * upload would be reviewed, and imported, under the new file's name. */
  const clearLoadedFile = () => {
    setCarriersFile(null);
    setCSVData([]);
    setCSVErrors([]);
    setColumns([]);
    setFieldMap({});
  };

  const handleCSVFile = async (file) => {
    try {
      clearLoadedFile();
      if (!file) return;
      if (file.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        return;
      }

      setLoading(true);
      setCarriersFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsed = parseCarrierCSV(event.target.result);
          setCSVErrors(parsed.errors);
          setCSVData(parsed.rows);
          setColumns(parsed.columns);
          setFieldMap(guessFieldMap(parsed.columns));
        } catch (err) {
          logError(
            "managerApp:carriers:CarrierImportDialog:parse",
            err,
            notify,
          );
          clearLoadedFile();
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        setLoading(false);
        clearLoadedFile();
        notify("Could not read that file.", "warning");
      };
      reader.readAsText(file);
    } catch (err) {
      logError(
        "managerApp:carriers:CarrierImportDialog:handleCSVFile",
        err,
        notify,
      );
      setLoading(false);
      clearLoadedFile();
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) handleCSVFile(acceptedFiles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ".csv",
    multiple: false,
    onDrop,
  });

  const handleClose = () => {
    clearLoadedFile();
    setIsOpen(false);
  };

  const handleSubmit = () => {
    handleImport(importable);
    handleClose();
  };

  return (
    <Dialog maxWidth="xl" open={isOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title}>Carriers Upload</Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container direction="row" spacing={2}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" gutterBottom>
                Instructions
              </Typography>
              <ol>
                <li>
                  Include the carrier's business name and dispatch email
                  address; a phone number is optional.
                </li>
                <li>
                  Save as a comma-separated-values (.CSV) file and upload here.
                </li>
                <li>
                  Match the spreadsheet columns to the carrier fields, then
                  review the rows below before importing.
                </li>
                <li>
                  Each imported carrier is emailed an invite to set up its
                  dispatch login.
                </li>
                <li>For help, contact us at 877-TRANSLOAD / 877-872-6756</li>
              </ol>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" gutterBottom>
                Upload Carrier CSV
              </Typography>
              <Box
                {...getRootProps()}
                className={`${classes.dropZone} ${
                  isDragActive ? classes.activeDropZone : ""
                }`}
              >
                <input {...getInputProps()} />
                <AttachFileIcon fontSize="large" />
                <Typography variant="body2" color="textSecondary">
                  {isDragActive
                    ? "Drop your CSV file here..."
                    : "Drag & drop a CSV file here, or click to select one"}
                </Typography>
              </Box>
              {carriersFile?.name && (
                <Typography variant="caption" className={classes.caption}>
                  Selected: {carriersFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          {(csvErrors.length > 0 || mappingErrors.length > 0) && (
            <Grid item xs={12}>
              <Typography variant="overline" color="error">
                Loading Errors:
              </Typography>
              <ul>
                {[...csvErrors, ...mappingErrors].map((err, idx) => (
                  <Typography key={idx} color="error">
                    <li>{err}</li>
                  </Typography>
                ))}
              </ul>
            </Grid>
          )}

          {columns.length > 0 && (
            <>
              <Grid item xs={12} className={classes.fakeFieldset}>
                <div id="field-map-legend" className={classes.fakeLegend}>
                  <Typography variant="overline" color="textSecondary">
                    Match Spreadsheet columns to fields
                  </Typography>
                </div>
                <Grid container spacing={1}>
                  {CARRIER_IMPORT_FIELDS.map((field) => (
                    <Grid key={field.id} item xs={12} md={3}>
                      <MuiTextField
                        select
                        id={`select_${field.id}`}
                        size="small"
                        margin="dense"
                        variant="outlined"
                        label={field.name}
                        fullWidth
                        value={fieldMap?.[field.id] ?? ""}
                        onChange={(e) =>
                          setFieldMap({
                            ...fieldMap,
                            [field.id]: e.target.value,
                          })
                        }
                        required={field.required}
                        helperText={false}
                      >
                        <MenuItem value="">
                          <span>&nbsp;</span>
                        </MenuItem>
                        {columns.map((col) => (
                          <MenuItem
                            key={`${field.id}_${col.id}`}
                            value={col.id}
                          >
                            {col.name}
                          </MenuItem>
                        ))}
                      </MuiTextField>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} className={classes.fakeFieldset}>
                <div id="import-preview-legend" className={classes.fakeLegend}>
                  <Typography variant="overline" color="textSecondary">
                    {`Review rows — ${importable.length} to import, ${skipped.length} skipped`}
                  </Typography>
                </div>
                <div className={classes.scrollableGrid}>
                  <ArrayField source="preview" record={{ preview: csvData }}>
                    <Datagrid rowClick={false} bulkActionButtons={false}>
                      <FunctionField
                        label="Status"
                        render={(record) => {
                          // The plan already decided this row — read its verdict
                          // rather than deciding again, so the grid and the
                          // counts above it can never disagree.
                          const reason = reasonByRowIndex.get(record.rowIndex);
                          return (
                            <Typography
                              variant="body2"
                              color={reason ? "error" : "textPrimary"}
                            >
                              {reason ? `Skipped — ${reason}` : "Import"}
                            </Typography>
                          );
                        }}
                      />
                      {columns.map((col) => (
                        <TextField
                          key={col.id}
                          source={col.id}
                          label={col.name}
                        />
                      ))}
                    </Datagrid>
                  </ArrayField>
                </div>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" autoFocus onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="IMPORT"
          color="primary"
          // A file-level parse error blocks the import as firmly as a missing
          // mapping does: a row with an unquoted comma has every field after it
          // shifted a column left, and a shifted row whose Email cell happens to
          // hold a valid-shaped address is importable on its face — it creates a
          // carrier and mails its dispatch invite to the wrong recipient. The
          // manager fixes the spreadsheet and re-uploads.
          disabled={
            loading ||
            csvErrors.length > 0 ||
            mappingErrors.length > 0 ||
            importable.length === 0
          }
          onClick={handleSubmit}
        >
          <AddAllIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
